import { useEffect, useRef, useState } from 'react';
import { App, Button, Drawer, Input, Tag } from 'antd';
import { DisconnectOutlined, SendOutlined, ThunderboltOutlined } from '@ant-design/icons';

interface WsMessage {
  id: string;
  direction: 'sent' | 'received' | 'system';
  text: string;
  timestamp: string;
}

type WsStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

const STATUS_COLOR: Record<WsStatus, string> = {
  idle: 'default',
  connecting: 'processing',
  open: 'success',
  closed: 'default',
  error: 'error',
};

const DIRECTION_STYLE: Record<WsMessage['direction'], string> = {
  system: 'text-gray-400',
  sent: 'text-blue-400',
  received: 'text-emerald-400',
};

const DIRECTION_ARROW: Record<WsMessage['direction'], string> = {
  system: '•',
  sent: '↑',
  received: '↓',
};

interface WebSocketDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * A real WebSocket client using the browser's native WebSocket API — the
 * connection goes directly from this tab to the target server, exactly
 * like a saved HTTP request executes server-side via the Laravel backend
 * for CORS/security reasons but a WebSocket handshake doesn't need that,
 * so there's no backend involvement here at all.
 */
export const WebSocketDrawer = ({ open, onClose }: WebSocketDrawerProps) => {
  const { message } = App.useApp();
  const [url, setUrl] = useState('wss://echo.websocket.org');
  const [protocols, setProtocols] = useState('');
  const [status, setStatus] = useState<WsStatus>('idle');
  const [messages, setMessages] = useState<WsMessage[]>([]);
  const [draft, setDraft] = useState('');
  const socketRef = useRef<WebSocket | null>(null);
  const logRef = useRef<HTMLDivElement>(null);

  const addMessage = (direction: WsMessage['direction'], text: string) => {
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), direction, text, timestamp: new Date().toISOString() },
    ]);
  };

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [messages]);

  useEffect(() => () => socketRef.current?.close(), []);

  const connect = () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      void message.error('Enter a WebSocket URL first');
      return;
    }
    if (!/^wss?:\/\//i.test(trimmedUrl)) {
      void message.error('URL must start with ws:// or wss://');
      return;
    }

    setStatus('connecting');
    addMessage('system', `Connecting to ${trimmedUrl}…`);

    const protocolList = protocols
      .split(',')
      .map((protocol) => protocol.trim())
      .filter(Boolean);

    const socket =
      protocolList.length > 0 ? new WebSocket(trimmedUrl, protocolList) : new WebSocket(trimmedUrl);
    socketRef.current = socket;

    socket.onopen = () => {
      setStatus('open');
      addMessage('system', 'Connected');
    };
    socket.onmessage = (event) => {
      addMessage('received', typeof event.data === 'string' ? event.data : '[binary data]');
    };
    socket.onerror = () => {
      setStatus('error');
      addMessage('system', 'Connection error');
    };
    socket.onclose = (event) => {
      setStatus('closed');
      addMessage(
        'system',
        `Disconnected (code ${event.code}${event.reason ? `: ${event.reason}` : ''})`,
      );
      socketRef.current = null;
    };
  };

  const disconnect = () => {
    socketRef.current?.close();
  };

  const send = () => {
    if (!draft.trim() || socketRef.current?.readyState !== WebSocket.OPEN) return;
    socketRef.current.send(draft);
    addMessage('sent', draft);
    setDraft('');
  };

  const isConnected = status === 'open';
  const isBusy = status === 'connecting';

  return (
    <Drawer title="WebSocket Client" open={open} onClose={onClose} size={720}>
      <div className="flex h-full flex-col gap-3">
        <div className="flex items-center gap-2">
          <Tag color={STATUS_COLOR[status]} className="shrink-0 capitalize">
            {status}
          </Tag>
          <Input
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="wss://example.com/socket"
            disabled={isConnected || isBusy}
            className="flex-1 font-mono"
          />
          {isConnected ? (
            <Button danger icon={<DisconnectOutlined />} onClick={disconnect}>
              Disconnect
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              loading={isBusy}
              onClick={connect}
            >
              Connect
            </Button>
          )}
        </div>

        <Input
          value={protocols}
          onChange={(event) => setProtocols(event.target.value)}
          placeholder="Sub-protocols (comma-separated, optional)"
          disabled={isConnected || isBusy}
          size="small"
        />

        <div
          ref={logRef}
          className="flex-1 overflow-y-auto rounded border border-black/10 p-2 font-mono text-xs dark:border-white/10"
        >
          {messages.length === 0 && (
            <span className="text-gray-400">No messages yet — connect and start sending.</span>
          )}
          {messages.map((msg) => (
            <div key={msg.id} className={`mb-1 ${DIRECTION_STYLE[msg.direction]}`}>
              <span className="mr-2 text-gray-500">
                {new Date(msg.timestamp).toLocaleTimeString()}
              </span>
              <span className="mr-1">{DIRECTION_ARROW[msg.direction]}</span>
              <span className="whitespace-pre-wrap break-all">{msg.text}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Input.TextArea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onPressEnter={(event) => {
              if (!event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
            placeholder={
              isConnected
                ? 'Type a message and press Enter to send…'
                : 'Connect first to send messages'
            }
            disabled={!isConnected}
            autoSize={{ minRows: 1, maxRows: 4 }}
            className="flex-1 font-mono text-xs"
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            disabled={!isConnected || !draft.trim()}
            onClick={send}
          >
            Send
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
